import { useRef, useState, useEffect } from 'react'
import ReactMarkdown from 'react-markdown'
import { ApiClient, type ClaudePendingAction } from '../api'
import { useMe } from '../hooks/useMe'
import { Icon } from '../components/Icon'

// ClaudeHelp — admin/manager-gated chat assistant for the events team. Talks to
// POST /api/claude-help (Claude Opus 4.8 + read-only DB tools, server-side).
// Phase 2: the assistant can PROPOSE changes; proposals come back as
// pendingActions and are only applied (via /claude-help/execute) when the user
// approves the confirm card below.

interface ChatTurn { role: 'user' | 'assistant'; content: string }
type ActionStatus = 'idle' | 'applying' | 'applied' | 'dismissed' | 'error'

const STARTERS = [
  'Which upcoming events still have roster gaps?',
  'How is our next big event selling against its capacity?',
  'Draft the overview and marketing sections of the Product Briefing for our next event.',
  'Suggest a festive catering upsell for the Santa events.'
]

export function ClaudeHelp() {
  const { data: me } = useMe()
  const [messages, setMessages] = useState<ChatTurn[]>([])
  const [input, setInput] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [tools, setTools] = useState<string[]>([])
  const [pending, setPending] = useState<ClaudePendingAction[]>([])
  const [actionState, setActionState] = useState<Record<number, { status: ActionStatus; error?: string }>>({})
  const scrollRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' })
  }, [messages, busy, pending])

  if (me && !me.isAdmin) {
    return (
      <div className="space-y-4">
        <h1 className="text-3xl">Claude Help</h1>
        <div className="bg-oxblood-soft border border-oxblood text-oxblood rounded p-4 text-sm">
          Claude Help is available to event managers and admins.
        </div>
      </div>
    )
  }

  async function send(text: string) {
    const trimmed = text.trim()
    if (!trimmed || busy) return
    const next: ChatTurn[] = [...messages, { role: 'user', content: trimmed }]
    setMessages(next)
    setInput('')
    setError(null)
    setPending([])
    setActionState({})
    setBusy(true)
    try {
      const res = await ApiClient.claudeHelp(next)
      const withReply: ChatTurn[] = res.reply.trim() ? [...next, { role: 'assistant', content: res.reply }] : next
      setMessages(withReply)
      setTools(res.toolsUsed ?? [])
      setPending(res.pendingActions ?? [])
    } catch (err) {
      setError((err as Error).message || 'Something went wrong.')
      setMessages(messages)
      setInput(trimmed)
    } finally {
      setBusy(false)
    }
  }

  async function applyAction(idx: number) {
    const action = pending[idx]
    if (!action || actionState[idx]?.status === 'applying' || actionState[idx]?.status === 'applied') return
    setActionState((s) => ({ ...s, [idx]: { status: 'applying' } }))
    try {
      const res = await ApiClient.claudeHelpExecute([{ type: action.type, payload: action.payload }])
      const r = res.results[0]
      if (r?.ok) {
        setActionState((s) => ({ ...s, [idx]: { status: 'applied' } }))
        // Record it in the conversation so later turns know it's saved (not just proposed).
        setMessages((m) => [...m, { role: 'assistant', content: `✓ Saved: ${r.summary ?? 'change applied.'}` }])
      } else {
        setActionState((s) => ({ ...s, [idx]: { status: 'error', error: r?.error ?? 'Could not apply.' } }))
      }
    } catch (err) {
      setActionState((s) => ({ ...s, [idx]: { status: 'error', error: (err as Error).message } }))
    }
  }

  function dismissAction(idx: number) {
    // Collapse the card WITHOUT executing — distinct 'dismissed' state so it's
    // never mistaken for a saved change.
    setActionState((s) => ({ ...s, [idx]: { status: 'dismissed' } }))
  }

  return (
    <div className="flex flex-col" style={{ height: 'calc(100vh - 8rem)' }}>
      <div className="flex items-start justify-between gap-4 flex-wrap mb-3">
        <div>
          <h1 className="text-3xl mb-1 flex items-center gap-2"><Icon name="info" size={24} className="text-green" /> Claude Help</h1>
          <p className="text-ink-soft text-sm max-w-2xl">
            Ask about events, sales, capacity, timetables, catering or rostering — get marketing copy and planning help — and
            propose changes (briefing drafts, catering, services) that you approve before anything is saved.
          </p>
        </div>
        {messages.length > 0 && (
          <button
            type="button"
            onClick={() => { setMessages([]); setTools([]); setError(null); setPending([]); setActionState({}) }}
            className="px-3 py-1.5 text-xs font-semibold rounded border border-line bg-white hover:bg-parchment transition-colors"
          >
            New chat
          </button>
        )}
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto bg-white border border-line rounded p-4 space-y-4">
        {messages.length === 0 && (
          <div className="h-full flex flex-col items-center justify-center text-center gap-4 py-10">
            <div className="w-14 h-14 rounded-full flex items-center justify-center bg-green-soft/60"><Icon name="info" size={26} className="text-green" /></div>
            <div>
              <div className="font-display text-lg">How can I help with events{me?.name ? `, ${me.name.split(' ')[0]}` : ''}?</div>
              <div className="text-sm text-ink-soft mt-1">Try one of these:</div>
            </div>
            <div className="grid gap-2 w-full max-w-xl">
              {STARTERS.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => send(s)}
                  className="text-left text-sm px-3 py-2 rounded border border-line hover:border-green hover:bg-parchment/50 transition-colors"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((m, i) => (
          <div key={i} className={m.role === 'user' ? 'flex justify-end' : 'flex justify-start'}>
            <div
              className={
                m.role === 'user'
                  ? 'max-w-[80%] rounded-2xl rounded-br-sm px-4 py-2.5 bg-green text-parchment text-sm whitespace-pre-wrap'
                  : 'max-w-[85%] rounded-2xl rounded-bl-sm px-4 py-3 bg-parchment/60 border border-line text-sm'
              }
            >
              {m.role === 'user'
                ? m.content
                : <div className="prose-sm"><ReactMarkdown>{m.content}</ReactMarkdown></div>}
            </div>
          </div>
        ))}

        {/* Proposed changes — require approval before anything is saved */}
        {pending.length > 0 && (
          <div className="space-y-2">
            {pending.map((a, idx) => {
              const st = actionState[idx]?.status ?? 'idle'
              return (
                <div key={idx} className="border rounded-lg p-3" style={{ borderColor: '#e3d6a8', background: '#fbf6e6' }}>
                  <div className="flex items-start gap-2">
                    <Icon name="edit" size={16} className="text-brass flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <div className="text-[11px] uppercase tracking-wide font-semibold text-ink-soft">Proposed change — needs your approval</div>
                      <div className="text-sm font-semibold mt-0.5">{a.summary}</div>
                      {st === 'error' && <div className="text-xs text-oxblood mt-1">{actionState[idx]?.error}</div>}
                    </div>
                    {st === 'applied' ? (
                      <span className="text-xs font-semibold text-green flex items-center gap-1 flex-shrink-0"><Icon name="check-circle" size={14} /> Saved</span>
                    ) : st === 'dismissed' ? (
                      <span className="text-xs font-semibold text-ink-soft flex-shrink-0">Dismissed</span>
                    ) : (
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        <button
                          type="button"
                          onClick={() => dismissAction(idx)}
                          disabled={st === 'applying'}
                          className="px-2.5 py-1 text-xs font-semibold rounded border border-line bg-white hover:bg-parchment transition-colors disabled:opacity-50"
                        >
                          Dismiss
                        </button>
                        <button
                          type="button"
                          onClick={() => applyAction(idx)}
                          disabled={st === 'applying'}
                          className="px-2.5 py-1 text-xs font-semibold rounded bg-green text-parchment hover:bg-green/90 disabled:opacity-50"
                        >
                          {st === 'applying' ? 'Applying…' : 'Apply'}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        )}

        {busy && (
          <div className="flex justify-start">
            <div className="rounded-2xl rounded-bl-sm px-4 py-3 bg-parchment/60 border border-line text-sm text-ink-soft font-mono">
              Claude is thinking…
            </div>
          </div>
        )}
      </div>

      {tools.length > 0 && !busy && (
        <div className="text-[11px] text-ink-soft mt-1.5 font-mono">Looked at: {Array.from(new Set(tools)).join(', ')}</div>
      )}
      {error && <div className="bg-oxblood-soft border border-oxblood text-oxblood rounded p-2.5 text-sm mt-2">{error}</div>}

      <form onSubmit={(e) => { e.preventDefault(); send(input) }} className="mt-3 flex items-end gap-2">
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(input) } }}
          rows={2}
          disabled={busy}
          placeholder="Ask about an event, sales, rostering — or ask Claude to draft a briefing or add catering…"
          className="flex-1 border border-line rounded px-3 py-2 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-green/30 focus:border-green disabled:bg-parchment/40"
        />
        <button
          type="submit"
          disabled={busy || input.trim().length === 0}
          className="px-4 py-2.5 text-sm font-semibold rounded bg-green text-parchment hover:bg-green/90 disabled:opacity-50"
        >
          {busy ? 'Sending…' : 'Send'}
        </button>
      </form>
      <p className="text-[11px] text-ink-soft mt-1.5">Claude can make mistakes, and only applies changes you approve — double-check anything important.</p>
    </div>
  )
}
